import * as THREE from 'three';
import { horn, spike, taperedTube, type ArmorKit } from './human-armors.js';
import type { HairFit, HairFitPart, SkullField } from './hair-fit.js';
import { ellipsoid } from './sdf.js';
import { HEADWEAR_HEAD, type HeadwearShape } from './headwear-fit.js';

/**
 * Headwear is its own slot: armor never includes a helm, hood or crown, and any of these goes with any armor. Each piece is built in
 * skull space from the same kit as the armors (see `human-armors.ts`). HEAD is the authored cage; `headwear-fit.ts` carries the
 * entire item and its hair room onto the wearer's actual sculpt and face warp. The head parts a piece hides are listed with its variant in
 * `src/content/characters/rigs/human-parts.ts`. Headwear is cosmetic: the book gives it no rules.
 */
const looks: Record<string, (k: ArmorKit) => void> = {};
const v3 = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

/** The sculpted skull (sculpt-space heights; `k.face` converts to skull space). */
const HEAD = HEADWEAR_HEAD;
/** The front of the skull's middle, with `margin` of clearance. */
const frontAt = (margin = 0) => HEAD.cz + HEAD.rz + margin;

/** A thin sheet with real edges; used for cloth folds and feather vanes instead of inflated solids. */
function sheet(rows: number, columns: number, at: (u: number, v: number) => THREE.Vector3) {
  const positions: number[] = [], uv: number[] = [], indices: number[] = [];
  for (let r = 0; r <= rows; r++) for (let c = 0; c <= columns; c++) {
    const u = c / columns, v = r / rows, p = at(u, v); positions.push(p.x, p.y, p.z); uv.push(u, v);
  }
  for (let r = 0; r < rows; r++) for (let c = 0; c < columns; c++) {
    const a = r * (columns + 1) + c, b = a + columns + 1; indices.push(a, b, a + 1, a + 1, b, b + 1);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2)); geometry.setIndex(indices); geometry.computeVertexNormals();
  return geometry;
}

/** Fit the whole item after its worn pitch, including rims and attachments, before the model batches its rigid meshes. */
function fitted(k: ArmorKit, build: (kit: ArmorKit) => void) {
  if (!k.headwearShape) { build(k); return; }
  const group = new THREE.Group(); k.skull.add(group);
  build({ ...k, skull: group, headwearShape: undefined });
  group.updateMatrixWorld(true);
  const worldToSkull = group.matrixWorld.clone().invert(), point = new THREE.Vector3();
  group.traverse(object => {
    if (!(object instanceof THREE.Mesh)) return;
    const localToSkull = worldToSkull.clone().multiply(object.matrixWorld), skullToLocal = localToSkull.clone().invert();
    const positions = object.geometry.getAttribute('position');
    for (let i = 0; i < positions.count; i++) {
      point.fromBufferAttribute(positions, i).applyMatrix4(localToSkull); point.y -= k.face(0);
      k.headwearShape!.to(point); point.y += k.face(0); point.applyMatrix4(skullToLocal);
      positions.setXYZ(i, point.x, point.y, point.z);
    }
    object.geometry.computeVertexNormals(); object.geometry.computeBoundingBox(); object.geometry.computeBoundingSphere();
  });
}

/**
 * The top of the skull grown by `margin`: a cap down to height `edge`. Its radii follow the sculpt, so it neither floats off the
 * head nor sinks into it.
 */
function cap(k: ArmorKit, material: THREE.Material, edge: number, margin: number) {
  const ry = HEAD.ry + margin, fall = Math.acos(THREE.MathUtils.clamp((edge - HEAD.cy) / ry, -1, 1));
  const shell = k.mesh(k.skull, new THREE.SphereGeometry(1, 36, 18, 0, Math.PI * 2, 0, fall), material, 0, k.face(HEAD.cy), HEAD.cz);
  shell.scale.set(HEAD.rx + margin, ry, HEAD.rz + margin); return shell;
}
/** A ring about the skull at height `y`, standing `margin` off its surface: a rim, a band or a brow ridge. */
function band(k: ArmorKit, material: THREE.Material, y: number, margin: number, thick = .005) {
  const rx = HEAD.rx + margin, rz = HEAD.rz + margin, ring = k.mesh(k.skull, new THREE.TorusGeometry(1, thick / ((rx + rz) / 2), 6, 40), material, 0, k.face(y), HEAD.cz);
  ring.rotation.x = Math.PI / 2; ring.scale.set(rx, rz, (rx + rz) / 2); return ring;
}
/** A guard behind and below a cap, covering the nape and the back of the ears. */
function nape(k: ArmorKit, material: THREE.Material, top: number, bottom: number, margin: number) {
  const guard = k.mesh(k.skull, new THREE.CylinderGeometry(1, .84, 1, 28, 1, true, Math.PI * .5, Math.PI), material, 0, k.face((top + bottom) / 2), HEAD.cz);
  guard.scale.set(HEAD.rx + margin, top - bottom, HEAD.rz + margin); return guard;
}
/** Steel for helms: lighter than the plate alloy so it reads as metal in a studio with nothing to reflect. */
const steelOf = (k: ArmorKit) => k.mat('alloy', .5, .3);

/** Rings of a hood from the crown to the hem: height, half width, half depth and the middle's z. Cosine-blended between keys. */
const HOOD_KEYS: number[][] = [[2.004, .001, .001, -.016], [1.86, .094, .114, -.022], [1.81, .104, .098, -.028], [1.76, .11, .088, -.04],
  [1.71, .13, .084, -.046], [1.66, .155, .078, -.044], [1.63, .172, .076, -.042]];
const HOOD_OPENING: [number, number, number] = [.066, 1.85, .112], MASKED_OPENING: [number, number, number] = [.06, 1.862, .098];
function hoodAt(y: number) {
  if (y >= 1.86) {
    const radius = Math.sqrt(Math.max(.0001, 1 - ((y - 1.892) / .112) ** 2));
    return [.098 * radius, .119 * radius, -.016 - .006 * Math.max(0, (1.892 - y) / .032)];
  }
  let i = 0; while (i < HOOD_KEYS.length - 2 && y < HOOD_KEYS[i + 1][0]) i++;
  const a = HOOD_KEYS[i], b = HOOD_KEYS[i + 1], t = THREE.MathUtils.clamp((a[0] - y) / (a[0] - b[0]), 0, 1), e = (1 - Math.cos(t * Math.PI)) / 2;
  return [1, 2, 3].map(c => a[c] + (b[c] - a[c]) * e);
}

/**
 * A hood: a cloth shell over the skull that flares down over the shoulders, with an oval opening for the face edged by a rolled rim.
 * `peak` pulls the back of the hood out into a point. The opening is [half width, centre height, half height].
 */
export function buildHood(k: ArmorKit, material: THREE.MeshStandardMaterial, o: { peak?: number; opening?: [number, number, number] } = {}) {
  fitted(k, raw => hoodShell(raw, material, o));
}
function hoodShell(k: ArmorKit, material: THREE.MeshStandardMaterial, o: { peak?: number; opening?: [number, number, number] }) {
  const cloth = material; cloth.side = THREE.DoubleSide;
  const at = hoodAt;
  const [ox, oy, oa] = o.opening ?? HOOD_OPENING;
  const rings = 64, around = 64, positions: number[] = [], indices: number[] = [], top = HOOD_KEYS[0][0], hem = HOOD_KEYS[HOOD_KEYS.length - 1][0];
  for (let r = 0; r <= rings; r++) {
    const y = top + (hem - top) * r / rings, [rx, rz, cz] = at(y);
    for (let c = 0; c <= around; c++) {
      const openingWidth = ox * Math.sqrt(Math.max(0, 1 - ((y - oy) / oa) ** 2));
      const edge = Math.asin(Math.min(.99, openingWidth / rx)), a = edge + c / around * (Math.PI * 2 - edge * 2);
      const back = Math.max(0, -Math.cos(a)) ** 1.5, rise = THREE.MathUtils.clamp((y - 1.84) / .164, 0, 1) ** 1.2;
      const fold = .0025 * Math.sin(a * 7 + y * 11) * Math.sin(a) ** 2 * Math.min(1, (top - y) / .04);
      const drape = .004 * Math.sin(a * 5) * THREE.MathUtils.clamp((1.84 - y) / .18, 0, 1);
      // A peak pulls the top of the back of the hood out and down into a point.
      positions.push((rx + fold + drape) * Math.sin(a), k.face(y) - (o.peak ?? 0) * .025 * rise * back,
        cz + (rz + fold + drape) * Math.cos(a) - (o.peak ?? 0) * .07 * rise * back);
    }
  }
  for (let r = 0; r < rings; r++) for (let c = 0; c < around; c++) {
    const a = r * (around + 1) + c, b = a + around + 1;
    indices.push(a, b, a + 1, a + 1, b, b + 1);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3)); geometry.setIndex(indices); geometry.computeVertexNormals();
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(positions.length / 3 * 2), 2));
  k.mesh(k.skull, geometry, cloth);
  // The rim rolls round the opening, lying on the shell.
  const loop: THREE.Vector3[] = [];
  for (let i = 0; i < 64; i++) {
    const a = i / 64 * Math.PI * 2, x = ox * Math.cos(a), y = oy + oa * Math.sin(a), [rx, rz, cz] = at(y);
    loop.push(v3(x, k.face(y), cz + rz * Math.sqrt(Math.max(0, 1 - (x / rx) ** 2)) + .002));
  }
  const edge = material.clone(); edge.color.multiplyScalar(.78);
  k.mesh(k.skull, new THREE.TubeGeometry(new THREE.CatmullRomCurve3(loop, true), 96, .0045, 6, true), edge);
}

/** A hood drawn up over the head, with a short drape over the shoulders. */
looks['hood'] = k => buildHood(k, k.mat('cloth', 0, .92), { peak: 0 });

/**
 * A rogue's hood: pulled low, with the opening narrowed, a point behind and a cloth mask over the nose, mouth and chin.
 */
looks['masked-hood'] = k => {
  const cloth = k.mat('cloth', 0, .92), mask = k.mat('cloth_dark', 0, .9); mask.side = THREE.DoubleSide;
  buildHood(k, cloth, { peak: 1, opening: MASKED_OPENING });
  // Cloth stretched across the lower face, turning back into the hood at the cheeks.
  k.mesh(k.skull, sheet(16, 48, (u, v) => {
    const a = (u - .5) * 2.9, front = Math.cos(a), top = 1.818 + .014 * front ** 4, bottom = 1.778 + .006 * (1 - front);
    const fold = .001 * Math.sin(v * Math.PI * 5 + Math.abs(a) * 1.8) * Math.sin(v * Math.PI);
    // Pull the cheek span in and taper toward the chin, leaving only a thin cloth clearance.
    return v3((.075 - .011 * v) * Math.sin(a), k.face(top + (bottom - top) * v), -.012 + (.112 - .024 * v + fold) * front);
  }), mask);
};

/** A ridge standing over the crown from brow to nape. */
function comb(k: ArmorKit, material: THREE.Material, height: number, thick = .006) {
  const points = Array.from({ length: 13 }, (_, i) => { const a = (i / 12 - .5) * Math.PI * .96; return v3(0, k.face(HEAD.cy + (HEAD.ry + .012 + height) * Math.cos(a)), HEAD.cz + (HEAD.rz + .012 + height) * Math.sin(a)); });
  return k.mesh(k.skull, taperedTube(new THREE.CatmullRomCurve3(points), t => thick * (.6 + .8 * Math.sin(t * Math.PI)), 24, 6), material);
}

/** Where each cap sits: its lower edge and how far it stands off the skull. The same numbers build the piece and leave room for the hair beneath it. */
const CAPS = { 'feathered-cap': { edge: 1.878, margin: .012 }, 'leather-cap': { edge: 1.88, margin: .011 }, skullcap: { edge: 1.885, margin: .01 }, 'kettle-hat': { edge: 1.9, margin: .01 },
  helm: { edge: 1.872, margin: .012 } } as const;
const BANDANA = { bottom: 1.854, top: 1.884, margin: .011 };

/** A bandana folded into a brow wrap, leaving the crown open, with a flat knot and two fabric tails. */
looks['bandana'] = k => {
  const wrap = k.mat('cloth', 0, .98); wrap.side = THREE.DoubleSide;
  k.mesh(k.skull, sheet(10, 64, (u, v) => {
    const a = u * Math.PI * 2, y = BANDANA.bottom + (BANDANA.top - BANDANA.bottom) * v + .002 * Math.sin(a * 3), radius = Math.sqrt(1 - ((y - HEAD.cy) / (HEAD.ry + BANDANA.margin)) ** 2);
    const fold = .0015 * Math.sin(v * Math.PI * 3 + Math.sin(a));
    return v3((HEAD.rx + BANDANA.margin + fold) * radius * Math.sin(a), k.face(y), HEAD.cz + (HEAD.rz + BANDANA.margin + fold) * radius * Math.cos(a));
  }), wrap);
  const back = HEAD.cz - HEAD.rz - .008;
  const knot = k.ellipsoid(k.skull, wrap, 0, k.face(1.867), back, .022, .012, .01); knot.rotation.z = .3;
  for (const side of [-1, 1]) {
    k.mesh(k.skull, sheet(18, 6, (u, v) => {
      const width = .018 * (1 - .65 * v), length = side < 0 ? .104 : .13;
      return v3(side * (.009 + .025 * v) + (u - .5) * width, k.face(1.861 - length * v + .008 * Math.sin(v * Math.PI) - .009 * Math.abs(u - .5) * v ** 6),
        back - .007 - .02 * Math.sin(v * Math.PI * .8) + .003 * Math.sin(u * Math.PI * 2 + v * 4));
    }), wrap);
  }
};

/** A soft cap with a feather: the sort worn by a rogue who would rather be noticed. */
looks['feathered-cap'] = k => {
  const cloth = k.mat('cloth_dark', 0, .9), feather = k.mat('trim', 0, .82), quill = k.mat('trim', 0, .72), trim = k.m.gold;
  feather.side = THREE.DoubleSide; quill.color.multiplyScalar(1.15);
  cap(k, cloth, CAPS['feathered-cap'].edge, CAPS['feathered-cap'].margin);
  band(k, k.m.leather, 1.88, .014, .009);
  const brim = k.mesh(k.skull, new THREE.CylinderGeometry(1, 1, .006, 40), cloth, 0, k.face(1.878), HEAD.cz); brim.scale.set(HEAD.rx + .034, 1, HEAD.rz + .036);
  k.ellipsoid(k.skull, trim, .086, k.face(1.94), .004, .007, .011, .011);
  const curve = new THREE.CatmullRomCurve3([v3(.078, k.face(1.955), 0), v3(.098, k.face(2.0), -.035), v3(.092, k.face(2.04), -.085), v3(.065, k.face(2.05), -.13)]);
  k.mesh(k.skull, taperedTube(curve, t => .0018 * (1 - .85 * t), 36, 6), quill);
  for (const side of [-1, 1]) {
    k.mesh(k.skull, sheet(64, 4, (u, v) => {
      const t = .13 + .87 * v, p = curve.getPoint(t), tangent = curve.getTangent(t), across = v3(0, -tangent.z, tangent.y).normalize();
      const width = .025 * Math.sin(v * Math.PI) ** .72 * (side < 0 ? .8 : 1);
      const notch = (Math.floor(v * 64) % 4 === 0 ? .88 : 1), offset = side * u * width * (1 + (notch - 1) * u ** 4);
      p.addScaledVector(across, offset).addScaledVector(tangent, -.012 * Math.sin(v * Math.PI) * u);
      p.x += .003 * Math.sin(u * Math.PI) * Math.sin(v * Math.PI); return p;
    }), feather);
    // Fine swept barbs catch the light without turning the vane into overlapping beads.
    for (let i = 1; i < 15; i++) {
      const v = i / 16, t = .13 + .87 * v, p = curve.getPoint(t), tangent = curve.getTangent(t), across = v3(0, -tangent.z, tangent.y).normalize();
      const tip = p.clone().addScaledVector(across, side * .024 * Math.sin(v * Math.PI) ** .72 * (side < 0 ? .8 : 1)).addScaledVector(tangent, -.012 * Math.sin(v * Math.PI));
      const mid = p.clone().lerp(tip, .5); mid.x += .0035; p.x += .0007; tip.x += .0007;
      k.mesh(k.skull, taperedTube(new THREE.CatmullRomCurve3([p, mid, tip]), t => .00045 * (1 - t * .75), 5, 3), quill);
    }
  }
};

/** Where the ear sits at the side of the head: pads and plates lie on it. */
const EAR_X = .082;

/** A close leather cap with ear flaps, stitched along the crown. */
looks['leather-cap'] = k => {
  const leather = k.m.leather, stitch = k.m.ivory;
  cap(k, leather, CAPS['leather-cap'].edge, CAPS['leather-cap'].margin); band(k, leather, 1.884, .015, .008);
  for (const side of [-1, 1]) {
    const flap = k.ellipsoid(k.skull, leather, side * (EAR_X + .004), k.face(HEAD.ears - .008), HEAD.cz + .008, .005, .03, .026); flap.rotation.x = .1;
    k.ellipsoid(k.skull, stitch, side * (EAR_X + .008), k.face(HEAD.ears - .035), HEAD.cz + .008, .003, .003, .003);
  }
  for (let i = -4; i <= 4; i++) { const a = i * .17; k.ellipsoid(k.skull, stitch, 0, k.face(HEAD.cy + (HEAD.ry + .008) * Math.cos(a)), HEAD.cz + (HEAD.rz + .008) * Math.sin(a), .004, .004, .004); }
};

/** A steel skullcap with a gilt rim and a button on top. */
looks['skullcap'] = k => {
  const steel = steelOf(k), gold = k.m.gold;
  cap(k, steel, CAPS.skullcap.edge, CAPS.skullcap.margin); band(k, gold, 1.886, .012, .005);
  k.ellipsoid(k.skull, gold, 0, k.face(HEAD.cy + HEAD.ry + .008), HEAD.cz, .01, .01, .01);
};

/** A kettle hat: a steel dome under a wide, level brim. */
looks['kettle-hat'] = k => {
  const steel = steelOf(k), gold = k.m.gold;
  cap(k, steel, CAPS['kettle-hat'].edge, CAPS['kettle-hat'].margin); band(k, gold, 1.902, .014, .005);
  const brim = k.mesh(k.skull, new THREE.CylinderGeometry(1, 1, .006, 40), steel, 0, k.face(1.9), HEAD.cz); brim.scale.set(.165, 1, .185);
  for (let i = 0; i < 10; i++) { const a = i / 10 * Math.PI * 2; k.ellipsoid(k.skull, gold, Math.sin(a) * .15, k.face(1.905), HEAD.cz + Math.cos(a) * .17, .005, .005, .005); }
};

/** The bottom of a bascinet's nape guard. */
const NAPE_BOTTOM = 1.795;
/** The body of a bascinet: an open steel helm with rounded cheek plates, a nasal bar and a nape guard. */
function bascinetBody(k: ArmorKit, steel: THREE.Material, gold: THREE.Material) {
  cap(k, steel, CAPS.helm.edge, CAPS.helm.margin); nape(k, steel, CAPS.helm.edge, NAPE_BOTTOM, CAPS.helm.margin);
  band(k, gold, 1.874, .015, .005);
  k.box(k.skull, steel, .009, .062, .008, 0, k.face(1.848), frontAt(.008));
  for (const side of [-1, 1]) {
    const plate = k.ellipsoid(k.skull, steel, side * (EAR_X + .005), k.face(1.838), HEAD.cz + .012, .004, .034, .044); plate.rotation.y = -side * .1;
    k.ellipsoid(k.skull, gold, side * (EAR_X + .009), k.face(1.862), HEAD.cz + .028, .003, .005, .005);
  }
}
/** A bascinet with a ridge over the crown. */
looks['bascinet'] = k => {
  const steel = steelOf(k), gold = k.m.gold;
  bascinetBody(k, steel, gold); comb(k, steel, .004, .007);
};

/** A horned helm: the bascinet's body with a pair of curved bone horns. */
looks['horned-helm'] = k => {
  const steel = steelOf(k), bone = k.m.ivory, gold = k.m.gold;
  bascinetBody(k, steel, gold);
  for (const side of [-1, 1]) horn(k, k.skull, [[side * (HEAD.rx + .006), k.face(1.915), HEAD.cz], [side * .135, k.face(1.935), HEAD.cz + .005], [side * .158, k.face(1.99), HEAD.cz + .01], [side * .13, k.face(2.06), HEAD.cz]], .02, .005, bone, 8);
};

/**
 * A winged helm: the bascinet's body with a pair of swept wings of overlapping gilt feathers fanning up and back from the temples, and a
 * low ridge over the crown.
 */
looks['winged-helm'] = k => {
  const steel = steelOf(k), gold = k.m.gold;
  bascinetBody(k, steel, gold); comb(k, steel, .006, .007);
  for (const side of [-1, 1]) {
    const base = v3(side * (HEAD.rx + .004), k.face(1.925), HEAD.cz + .01);
    const up = v3(side * .5, .85, -.18).normalize(), back = v3(side * .22, .08, -.97).normalize();
    for (let row = 0; row < 2; row++) for (let i = 0; i < 6; i++) {
      // Feathers fan from straight up to straight back; the lower row is shorter and lies beneath the upper.
      const t = i / 5 * 1.25, length = (.21 - i * .016) * (row ? .72 : 1), dir = up.clone().multiplyScalar(Math.cos(t)).addScaledVector(back, Math.sin(t)).normalize();
      const feather = k.ellipsoid(k.skull, gold, 0, 0, 0, .018 + row * .003, length / 2, .0035);
      feather.quaternion.setFromUnitVectors(v3(0, 1, 0), dir); feather.rotateY(side * .35);
      feather.position.copy(base).addScaledVector(dir, length / 2 + .006).add(v3(0, -.014 * row, .004 * row));
    }
    k.ellipsoid(k.skull, gold, side * (HEAD.rx + .008), k.face(1.925), HEAD.cz + .01, .012, .018, .018);
  }
};

/** A great helm's barrel: its lower rim and flat top, and its half widths. */
const GREAT_HELM = { bottom: 1.73, top: 1.985, rx: HEAD.rx + .015, rz: HEAD.rz + .016 };
/**
 * A great helm: a closed steel barrel with a flat riveted top, gilt bands, a cross of slits for the eyes and nose, breath holes down
 * the jaw and a low comb. It hides the face, hair and beard.
 */
looks['great-helm'] = k => {
  const steel = steelOf(k), gold = k.m.gold, dark = k.m.dark;
  const { bottom, top, rx, rz } = GREAT_HELM, mid = k.face((top + bottom) / 2 - .02);
  const front = (x: number) => HEAD.cz + rz * Math.sqrt(Math.max(0, 1 - (x / rx) ** 2));
  const barrel = k.mesh(k.skull, new THREE.CylinderGeometry(.96, 1.02, top - bottom - .04, 40), steel, 0, mid, HEAD.cz); barrel.scale.set(rx, 1, rz);
  const crown = k.mesh(k.skull, new THREE.SphereGeometry(1, 40, 10, 0, Math.PI * 2, 0, Math.PI * .5), steel, 0, k.face(top - .052), HEAD.cz); crown.scale.set(rx * .96, .05, rz * .96);
  const slit = k.mesh(k.skull, new THREE.CylinderGeometry(1, 1, .014, 24, 1, true, -.78, 1.56), dark, 0, k.face(HEAD.eyes), HEAD.cz); slit.scale.set(rx + .002, 1, rz + .002);
  k.box(k.skull, dark, .013, .105, .008, 0, k.face(1.808), front(0) + .003);
  for (let row = 0; row < 4; row++) for (const side of [-1, 1]) for (let col = 0; col < 2; col++) { // breath holes down the jaw
    const x = side * (.034 + col * .022); k.ellipsoid(k.skull, dark, x, k.face(1.8 - row * .016), front(x) + .002, .0045, .0045, .003);
  }
  for (const y of [1.94, 1.77]) {
    const strap = k.mesh(k.skull, new THREE.CylinderGeometry(1, 1, .014, 40, 1, true), gold, 0, k.face(y), HEAD.cz); strap.scale.set(rx + .003, 1, rz + .003);
    for (let i = 0; i < 18; i++) { const a = i / 18 * Math.PI * 2; k.ellipsoid(k.skull, gold, Math.sin(a) * (rx + .004), k.face(y), HEAD.cz + Math.cos(a) * (rz + .004), .0045, .0045, .0045); }
  }
  const lip = k.mesh(k.skull, new THREE.CylinderGeometry(1, 1, .012, 40, 1, true), gold, 0, k.face(bottom + .005), HEAD.cz); lip.scale.set(rx * 1.05, 1, rz * 1.05);
  const rib = k.mesh(k.skull, new THREE.CylinderGeometry(1, 1.05, top - bottom - .06, 6, 1, true, -.05, .1), gold, 0, mid, HEAD.cz); rib.scale.set(rx + .004, 1, rz + .004);
  k.mesh(k.skull, new THREE.BoxGeometry(.008, .02, rz * 1.4), steel, 0, k.face(top - .004), HEAD.cz);
  k.ellipsoid(k.skull, gold, 0, k.face(top + .012), HEAD.cz + rz * .55, .007, .007, .007);
};

/** A gilt circlet with a jewel at the brow and a small swept wing at each side. It hides nothing. */
looks['circlet'] = k => {
  const gold = k.m.gold, gem = k.m.gem;
  band(k, gold, 1.91, .005, .0045);
  k.ellipsoid(k.skull, gem, 0, k.face(1.915), frontAt(.004) - .006, .011, .015, .008);
  for (const side of [-1, 1]) {
    spike(k, k.skull, gold, [side * .03, k.face(1.915), frontAt(.004) - .012], .007, .04, -side * .3, 0, 4);
    spike(k, k.skull, gold, [side * (HEAD.rx + .004), k.face(1.915), HEAD.cz + .01], .009, .06, -side * 1.1, 0, 4);
  }
};

/** How far each piece is pitched about the skull's centre (negative drops the back): worn level, a hat looks pasted on. */
const PITCH: Record<string, number> = { bandana: -.2, 'feathered-cap': -.16, 'leather-cap': -.16, skullcap: -.18, 'kettle-hat': -.14, bascinet: -.1, 'horned-helm': -.1, 'winged-helm': -.1, 'great-helm': -.05, circlet: -.16 };
/** A kit whose skull is pitched about the middle of the cranium, so everything built on it tilts with it. */
function pitched(k: ArmorKit, pitch: number): ArmorKit {
  const group = new THREE.Group(); k.skull.add(group);
  const pivot = v3(0, k.face(HEAD.cy), HEAD.cz), turn = new THREE.Quaternion().setFromAxisAngle(v3(1, 0, 0), pitch);
  group.quaternion.copy(turn); group.position.copy(pivot).sub(pivot.clone().applyQuaternion(turn));
  return { ...k, skull: group };
}


/** How much room hair keeps between its inner surface and a piece's outer one: the thickness of the shell and a little air. */
const ROOM = .003;
/** Ramp over which hair is pressed in under a rim: it runs out from beneath the edge instead of ending on a cut. */
const RIM = .012;
const smoothstep = (v: number, a: number, b: number) => { const t = THREE.MathUtils.clamp((v - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };

/** A part of a fit, seen from a piece pitched about the middle of the cranium: points are carried into the piece's own frame first. */
function pitchedPart(pitch: number, part: HairFitPart): HairFitPart {
  if (!pitch) return part;
  const c = Math.cos(pitch), s = Math.sin(pitch);
  const local = (f: SkullField): SkullField => (x, y, z) => { const dy = y - HEAD.cy, dz = z - HEAD.cz; return f(x, HEAD.cy + dy * c + dz * s, HEAD.cz - dy * s + dz * c); };
  return { inner: local(part.inner), cover: local(part.cover) };
}
/** A cap, helm dome or band: the top of the skull grown by `margin`, holding the hair above its lower edge. */
function domePart(edge: number, margin: number): HairFitPart {
  const room = ellipsoid([0, HEAD.cy, HEAD.cz], [HEAD.rx + margin - ROOM, HEAD.ry + margin - ROOM, HEAD.rz + margin - ROOM]).field;
  return { inner: room, cover: (_x, y) => smoothstep(y, edge - RIM, edge + .003) };
}
/** Signed distance (scaled by the smaller half width) to an ellipse of half widths `a` and `b` about (0, cz), kept clear of the degenerate rings at a shell's apex. */
function ellipseSide(x: number, z: number, cz: number, a: number, b: number) {
  const ra = Math.max(a - ROOM, .02), rb = Math.max(b - ROOM, .02);
  return (Math.sqrt((x / ra) ** 2 + ((z - cz) / rb) ** 2) - 1) * Math.min(ra, rb);
}
/** An elliptical drum about the head, `rx` and `rz` wide, from `bottom` up to `top`, closed by a flat top. */
function drumPart(rx: number, rz: number, bottom: number, top: number): HairFitPart {
  return {
    inner: (x, y, z) => Math.max(ellipseSide(x, z, HEAD.cz, rx, rz), y - (top - ROOM)),
    cover: (_x, y) => smoothstep(y, bottom - .002, bottom + RIM),
  };
}
/** A bascinet's nape guard: a tapering half drum behind the head, from the dome down to `NAPE_BOTTOM`. */
function guardPart(): HairFitPart {
  const { edge, margin } = CAPS.helm, rx = HEAD.rx + margin, rz = HEAD.rz + margin;
  return {
    inner: (x, y, z) => { const width = .84 + .16 * THREE.MathUtils.clamp((y - NAPE_BOTTOM) / (edge - NAPE_BOTTOM), 0, 1); return ellipseSide(x, z, HEAD.cz, rx * width, rz * width); },
    // Only the back of the head: the cheek plates and the open face are left alone.
    cover: (_x, y, z) => smoothstep(y, NAPE_BOTTOM, NAPE_BOTTOM + RIM) * smoothstep(HEAD.cz - z, -.004, .02),
  };
}
/**
 * A hood: its shell down to the hem, open at the face, where the hair shows. Hair stays under a hood, down to the chest where it would
 * hang: a lock or plait tucked into the hood is hidden with the rest rather than left lying on top of the cloth.
 */
function hoodPart(opening: [number, number, number]): HairFitPart {
  const [ox, oy, oa] = opening, hem = HOOD_KEYS[HOOD_KEYS.length - 1][0], top = HOOD_KEYS[0][0];
  return {
    inner: (x, y, z) => {
      const [rx, rz, cz] = hoodAt(THREE.MathUtils.clamp(y, hem, top));
      return Math.max(ellipseSide(x, z, cz, rx, rz), y - (top - ROOM));
    },
    cover: (x, y, z) => {
      const open = z > hoodAt(THREE.MathUtils.clamp(y, hem, top))[2] ? smoothstep(Math.hypot(x / ox, (y - oy) / oa), .84, 1) : 1;
      return smoothstep(y, hem - .17, hem - .14) * open;
    },
  };
}
/** The hairs' fit under each piece of headwear; a circlet sits on the hair and a piece not listed leaves it alone. */
const FITS: Record<string, { key: string; parts: HairFitPart[] }> = {};
const fitOf = (id: string, parts: HairFitPart[]) => { FITS[id] = { key: id, parts }; };
for (const id of ['feathered-cap', 'leather-cap', 'skullcap', 'kettle-hat'] as const) fitOf(id, [pitchedPart(PITCH[id], domePart(CAPS[id].edge, CAPS[id].margin))]);
fitOf('bandana', [pitchedPart(PITCH.bandana, {
  inner: domePart(BANDANA.bottom, BANDANA.margin).inner,
  cover: (x, y, z) => {
    const a = Math.atan2(x, z - HEAD.cz), height = y - .002 * Math.sin(a * 3);
    return smoothstep(height, BANDANA.bottom - .004, BANDANA.bottom + .004) * (1 - smoothstep(height, BANDANA.top - .004, BANDANA.top + .004));
  },
})]);
for (const id of ['bascinet', 'horned-helm', 'winged-helm']) fitOf(id, [pitchedPart(PITCH[id], domePart(CAPS.helm.edge, CAPS.helm.margin)), pitchedPart(PITCH[id], guardPart())]);
fitOf('great-helm', [pitchedPart(PITCH['great-helm'], drumPart(GREAT_HELM.rx, GREAT_HELM.rz, GREAT_HELM.bottom, GREAT_HELM.top))]);
fitOf('hood', [hoodPart(HOOD_OPENING)]);
fitOf('masked-hood', [hoodPart(MASKED_OPENING)]);

/** How the hair beneath a piece of headwear is held, or undefined where the piece leaves it be. */
export function headwearHairFit(id: string, shape?: HeadwearShape): HairFit | undefined {
  const fit = FITS[id]; if (!fit || !shape) return fit;
  const field = (source: SkullField): SkullField => (x, y, z) => { const p = shape.from(v3(x, y, z)); return source(p.x, p.y, p.z); };
  return { key: `${fit.key}:${shape.key}`, parts: fit.parts.map(part => ({ inner: field(part.inner), cover: field(part.cover) })) };
}

/** Puts the chosen headwear on; `none` and unknown ids draw nothing. */
export function dressHeadwear(id: string, kit: ArmorKit): boolean {
  const look = looks[id]; if (!look) return false;
  fitted(kit, raw => look(PITCH[id] ? pitched(raw, PITCH[id]) : raw)); return true;
}
export const headwearIds = Object.keys(looks);
